import {catchError, filter, switchMap, take} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';
import {AuthorizationService} from './authorization.service';
import {AppConstants} from '../commons/app-constants';
import {LocalStorageService} from '../services/local-storage/local-storage.service';
import {NavController} from '@ionic/angular';
import {isDefined} from '../commons/utils';
import {DeviceStatus} from '../domain/device-status';
import {AppPaths} from '../commons/app-paths';
import {PrincipalService} from '../services/principal/principal.service';
import {UserDeviceService} from '../services/user-device/user-device.service';
import {DeviceService} from '../services/device/device.service';
import {AppLanguageService} from '../services/app-language/app-language.service';
import {CacheService} from '../services/cache/cache.service';
import {SharedDataService} from '../services/shared-data/shared-data.service';
import {EtHttpService} from '../services/et-http/et-http.service';
import {ConfigurationService} from '../services/configuration/configuration.service';
import {TokenResponse} from '@openid/appauth';
import {PinCodeService} from '../services/pin-code/pin-code.service';
import {GlobalizationService} from '../services/globalization/globalization.service';
import {Observable, of, Subscription} from 'rxjs';
import {AlertService} from '../services/alert/alert.service';
import {CompanyService} from '../services/company/company.service';
import {UserUtils} from '../utils/user-utils';
import {MobileVersionStatus} from '../domain/mobile-version-status';
import {ModalConfigs} from '../commons/modal-configs';
import {ModalService} from '../services/modal/modal.service';
import {LoggerService} from '../services/logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService {
  private subscription: Subscription;

  constructor(
    private readonly authorizationService: AuthorizationService,
    private readonly navCtrl: NavController,
    private readonly localStorage: LocalStorageService,
    private readonly principal: PrincipalService,
    private readonly userDeviceService: UserDeviceService,
    private readonly deviceService: DeviceService,
    private readonly appLanguage: AppLanguageService,
    private readonly cacheService: CacheService,
    private readonly sharedDataService: SharedDataService,
    private readonly http: EtHttpService,
    private readonly configurationService: ConfigurationService,
    private readonly pinCodeService: PinCodeService,
    private readonly globalizationService: GlobalizationService,
    private readonly alertService: AlertService,
    private readonly companyService: CompanyService,
    private readonly modalService: ModalService,
    private readonly logger: LoggerService
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.http.initialize().pipe(catchError(() => of(null))).subscribe(() => {
      this.checkAppVersion();
      if (this.localStorage.isStorageAvailable()) {
        if (isDefined(this.localStorage.getItem(AppConstants.SECURED_TOKEN))) {
          this.navCtrl.navigateRoot(AppPaths.LOGIN_PIN_CODE_PAGE);
        } else {
          this.subscription = this.authorizationService
            .getAuthService().isAuthenticated$
            .subscribe(authenticated =>
              authenticated
                ? this.authorizationService
                    .getAuthService()
                    .token$.pipe(take(1))
                    .subscribe(token =>
                      token?.accessToken
                        ? this.checkAuthState(token)
                        : this.navCtrl.navigateRoot(
                            AppPaths.LOGIN_INIT_PAGE
                          )
                    )
                : this.navCtrl.navigateRoot(
                    AppPaths.LOGIN_INIT_PAGE
                  )
            );
        }
      }
      if (isDefined(this.subscription)) {
        this.subscription.unsubscribe();
      }
    });
    return this.authorizationService.getAuthService().isAuthenticated$;
  }

  private checkAppVersion(): void {
    this.configurationService.getMobileAppLatestVersion()
      .pipe(catchError(error => {
        this.logger.error('Failed to check mobile app version', error);
        return of(null);
      }))
      .subscribe(status => {
        switch (status) {
          case MobileVersionStatus.LATEST:
            break;
          case MobileVersionStatus.SUPPORTED:
            this.alertService.presentAlertUpdateVersion();
            break;
          case MobileVersionStatus.UNSUPPORTED:
            this.modalService.showModal(ModalConfigs.deprecatedAppVersion);
            break;
        }
      });
  }

  public checkAuthState(tokenResponse: TokenResponse): void {
    this.sharedDataService.setSharedData<string>(
      AppConstants.ACCESS_TOKEN,
      tokenResponse.accessToken
    );
    this.authorizationService.clearAuthStorage();
    this.principal
      .identity()
      .pipe(filter(user => isDefined(user)))
      .subscribe(user => {
        this.appLanguage
          .setTranslationLanguage(user.langKey)
          .subscribe(() =>
            this.globalizationService.initGlobalization()
          );
        this.cacheService.clearCache();
        const defaultTenant = UserUtils.getDefaultTenant(user);
        this.companyService
          .getRootCompany(defaultTenant)
          .subscribe(() => {
            this.authorizationService
              .applyPostLoginActions()
              .pipe(
                switchMap(() =>
                  this.deviceService.getDeviceId()
                )
              )
              .subscribe(deviceId => {
                this.userDeviceService
                  .getUserDevice(user.userId, deviceId)
                  .subscribe(device => {
                    if (isDefined(device)) {
                      if (
                        device.status ===
												DeviceStatus.INACTIVE
                      ) {
                        this.navCtrl.navigateForward(
                          AppPaths.DEVICE_REGISTRATION_PAGE
                        );
                      } else {
                        this.pinCodeService
                          .hasPin()
                          .subscribe(
                            () => {
                              this.navCtrl.navigateRoot(
                                AppPaths.LOGIN_PIN_CODE_PAGE
                              );
                            },
                            () => {
                              this.navCtrl.navigateForward(
                                AppPaths.SET_PIN_CODE_PAGE
                              );
                            }
                          );
                      }
                    } else {
                      this.navCtrl.navigateForward(
                        AppPaths.DEVICE_REGISTRATION_PAGE
                      );
                    }
                    if (isDefined(this.subscription)) {
                      this.subscription.unsubscribe();
                    }
                  });
              });
          });
      });
  }
}
